import { v4 as uuid } from 'uuid';
import {
  MentionAtomExtension,
  NamedMentionAtomNodeAttributes,
} from '@remirror/extension-mention-atom';
import { Node } from '@remirror/pm/model';
import { EditorState } from '@remirror/pm/state';
import { ReactExtensions } from '@remirror/react';
import { findChildren, NodeWithPosition, RemirrorManager } from 'remirror';
import { CommentMentionUser } from '../components/CommentMentionSuggestor';
import EmptyExtension from '../extensions/EmptyExtension';

const GLOBAL_FULL_USER_MENTION_REGEX = /(@\[__user__\]\(\S*\))/g;
const FULL_USER_MENTION_REGEX = /(@\[__user__\]\(\S*\))/;
export const USER_LABEL_REGEX = /^@\[__user__\]\((\S*)\)$/;
const NEW_LINE = '\n';

/**
 * Function to convert text into an array of ProseMirror nodes
 */
export const getNodesFromText = (
  manager: RemirrorManager<
    ReactExtensions<MentionAtomExtension | EmptyExtension>
  >,
  text: string,
  mentionUsers: CommentMentionUser[]
): Node[] => {
  if (text.trim().length === 0) {
    return [];
  }

  // Split the input text into lines using a regular expression
  const lineSplitRegex = /(?:\r\n|\r|\n)/g;
  const lines = text.split(lineSplitRegex);

  const nodes: Node[] = [];

  lines.forEach(line => {
    // If the line is empty, add an empty paragraph node
    if (!line) {
      nodes.push(Node.fromJSON(manager.schema, { type: 'paragraph' }));
    } else {
      const containsMention = FULL_USER_MENTION_REGEX.test(line);
      if (!containsMention) {
        // Otherwise, create a new paragraph node with the line text as content
        const content = manager.schema.text(line);
        const paragraph = manager.schema.nodes.paragraph.create({}, content);
        nodes.push(paragraph);
      } else {
        const nodesWithinParagraph: Node[] = [];

        const allMatches = Array.from(
          line.matchAll(GLOBAL_FULL_USER_MENTION_REGEX)
        );

        /**
         * From a string, find chunks of string + mention
         * (String text appended before a mention)
         * i.e. "some text @[__user__](id)"
         *
         * After each chunk, reset the offset
         * to analyze the next chunk within a line of text
         * until finished
         */
        let offset = 0;
        allMatches.forEach(match => {
          const startIndex = match.index;
          const startText = line.slice(offset, startIndex);
          if (startText)
            nodesWithinParagraph.push(manager.schema.text(startText));

          /**
           * Find the mention with metadata
           */
          const formattedTextResults = getFormattedText(match[0]);
          formattedTextResults.forEach(formattedText => {
            if (formattedText.isMention) {
              const existingUser = mentionUsers.find(
                mentionUser => mentionUser.id === formattedText.userId
              );
              if (existingUser) {
                nodesWithinParagraph.push(
                  manager.schema.node('mentionAtom', {
                    id: existingUser.id,
                    label: existingUser.label,
                    name: 'at',
                  })
                );
              }
            }
          });

          offset += startText.length + match[0].length;
        });

        /**
         * Remaining text after last mention
         */
        const endText = line.slice(offset);
        if (endText) nodesWithinParagraph.push(manager.schema.text(endText));

        const paragraph = manager.schema.nodes.paragraph.create(
          {},
          nodesWithinParagraph
        );

        nodes.push(paragraph);
      }
    }
  });

  return nodes;
};

/**
 * Function to convert ProsemirrorState into plain text
 */
export const getTextFromState = (state: EditorState): string => {
  let content = '';
  let isFirstParagraph = true;

  state.doc.forEach((node: Node) => {
    if (node.type.name === 'paragraph') {
      // Add a newline before each paragraph (except the first one)
      if (!isFirstParagraph) {
        content += NEW_LINE;
      } else {
        isFirstParagraph = false;
      }
      findChildren({
        node,
        predicate: child => child.node.isText || child.node.isAtom,
      }).forEach((innerNode: NodeWithPosition) => {
        // do nothing
        const { node: childNode } = innerNode;

        if (childNode.type.isText) {
          content += childNode.textContent;
        } else if (
          childNode.type.isAtom &&
          childNode.type.name === 'mentionAtom'
        ) {
          const attrs = childNode.attrs as NamedMentionAtomNodeAttributes;
          /**
           * Note: we can choose an arbitrary format to transform remirror state -> text
           * Took some inspo from another lib react-mentions
           * But obvi we can pick whatever makes sense
           * https://github.com/signavio/react-mentions/blob/master/demo/src/examples/CutCopyPaste.js
           *
           * It may makes sense to only go by id as source of truth since name/label can change/get stale
           */
          content += `@[__user__](${attrs.id})`;
        }
      });
    }
  });

  return content;
};

export const getFormattedText = (text: string) => {
  const splitContent = text.split(FULL_USER_MENTION_REGEX);

  const result = splitContent.map(partialText => {
    const isMention = FULL_USER_MENTION_REGEX.test(partialText);
    let userId: string | undefined;
    if (isMention) {
      const match = partialText.match(USER_LABEL_REGEX);
      userId = match ? match[1] : '';
    }
    return {
      text: partialText,
      isMention,
      userId,
      id: uuid(),
    };
  });
  return result;
};

export const getCharacterCountFromState = (state: EditorState) => {
  let count = 0;
  state.doc.forEach((node: Node) => {
    if (node.type.name === 'paragraph') {
      findChildren({
        node,
        predicate: child => child.node.isText || child.node.isAtom,
      }).forEach((innerNode: NodeWithPosition) => {
        const { node: childNode } = innerNode;
        if (childNode.type.isText) {
          count += childNode.textContent.length;
        } else if (
          childNode.type.isAtom &&
          childNode.type.name === 'mentionAtom'
        ) {
          count += 1;
        }
      });
    }
  });
  return count;
};

export default {
  getTextFromState,
  getFormattedText,
  getNodesFromText,
  getCharacterCountFromState,
};
